import React, { useState, useMemo } from 'react';
import './WhiskyList.css';

const WhiskyList = ({ whiskies, ratings, onWhiskySelect, onClearRating, onAddWhisky, onDeleteWhisky, onEditWhisky }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmClear, setConfirmClear] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showComments, setShowComments] = useState(true);

  // Cookie utility functions
  const setCookie = (name, value, days = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  };

  // Initialize sortBy from cookie or default to 'name'
  const [sortBy, setSortBy] = useState(() => {
    return getCookie('whisky_sort_preference') || 'name';
  });

  // Fast search and filter
  const filteredWhiskies = useMemo(() => {
    if (!searchTerm.trim()) return whiskies;
    
    const term = searchTerm.toLowerCase();
    return whiskies.filter(whisky => 
      whisky.name?.toLowerCase().includes(term) ||
      whisky.distillery?.toLowerCase().includes(term) ||
      whisky.region?.toLowerCase().includes(term) ||
      whisky.type?.toLowerCase().includes(term)
    );
  }, [whiskies, searchTerm]);

  // Sort whiskies
  const sortedWhiskies = useMemo(() => {
    const sorted = [...filteredWhiskies];
    
    switch (sortBy) {
      case 'name':
        return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      case 'distillery':
        return sorted.sort((a, b) => (a.distillery || '').localeCompare(b.distillery || ''));
      case 'rating':
        return sorted.sort((a, b) => {
          const ratingA = ratings[a.id]?.overall || 0;
          const ratingB = ratings[b.id]?.overall || 0;
          return ratingB - ratingA;
        });
      case 'rated':
        return sorted.sort((a, b) => {
          const hasRatingA = ratings[a.id] ? 1 : 0;
          const hasRatingB = ratings[b.id] ? 1 : 0;
          return hasRatingB - hasRatingA;
        });
      default:
        return sorted;
    }
  }, [filteredWhiskies, sortBy, ratings]);

  const getRatingDisplay = (whisky) => {
    const rating = ratings[whisky.id];
    if (!rating) return null;
    
    const total = rating.nose + rating.palate + rating.finish + rating.overall;
    return {
      total,
      average: (total / 4).toFixed(1)
    };
  };

  const handleClearClick = (e, whisky) => {
    e.stopPropagation();
    setConfirmClear(whisky);
  };

  const handleDeleteClick = (e, whisky) => {
    e.stopPropagation();
    setConfirmDelete(whisky);
  };

  const handleEditClick = (e, whisky) => {
    e.stopPropagation();
    onEditWhisky(whisky);
  };

  const confirmClearRating = () => {
    if (confirmClear && onClearRating) {
      onClearRating(confirmClear.id);
    }
    setConfirmClear(null);
  };

  const cancelClear = () => {
    setConfirmClear(null);
  };

  const confirmDeleteWhisky = () => {
    if (confirmDelete && onDeleteWhisky) {
      onDeleteWhisky(confirmDelete.id);
    }
    setConfirmDelete(null);
  };

  const cancelDelete = () => {
    setConfirmDelete(null);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCookie('whisky_sort_preference', newSortBy);
  };


  if (whiskies.length === 0) {
    return (
      <div className="empty-state">
        <h3>No Whiskies in Collection</h3>
        <p>Start building your whisky collection by adding your first whisky!</p>
        <button className="btn btn-primary add-first-whisky" onClick={onAddWhisky}>
          ➕ Add Your First Whisky
        </button>
      </div>
    );
  }

  return (
    <div className="whisky-list">
      <div className="search-section">
        <input
          type="text"
          className="input search-input"
          placeholder="Search whiskies, distilleries, regions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        <div className="sort-controls">
          <select
            className="input sort-select"
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
          >
            <option value="name">Sort by Name</option>
            <option value="distillery">Sort by Distillery</option>
            <option value="rating">Sort by Rating</option>
            <option value="rated">Rated First</option>
          </select>
        </div>
      </div>

      <div className="results-info">
        <span>{sortedWhiskies.length} whiskies</span>
        <span>{Object.keys(ratings).length} rated</span>
        <div className="results-controls">
          <button 
            className={`toggle-button ${showComments ? 'active' : ''}`}
            onClick={() => setShowComments(!showComments)}
            title="Toggle comments display"
          >
            💭
          </button>
          <button 
            className="add-whisky-button"
            onClick={onAddWhisky}
            title="Add new whisky"
          >
            ➕ Add Whisky
          </button>
        </div>
      </div>

      <div className="whisky-grid">
        {sortedWhiskies.map((whisky) => {
          const ratingData = getRatingDisplay(whisky);
          
          return (
            <div
              key={whisky.id}
              className={`whisky-card ${ratingData ? 'rated' : ''}`}
              onClick={() => onWhiskySelect(whisky)}
            >
              <div className="whisky-header">
                <h3 className="whisky-name">
                  {whisky.name}
                </h3>
                <div className="header-actions">
                  {ratingData && (
                    <div className="rating-badge">
                      {ratingData.total}/100
                    </div>
                  )}
                  <div className="whisky-actions">
                    <button
                      className="edit-btn"
                      onClick={(e) => handleEditClick(e, whisky)}
                      title="Edit whisky details"
                    >
                      ✏️
                    </button>
                    <button
                      className="delete-btn"
                      onClick={(e) => handleDeleteClick(e, whisky)}
                      title="Delete whisky"
                    >
                      🗑️
                    </button>
                    {ratingData && (
                      <button
                        className="clear-btn"
                        onClick={(e) => handleClearClick(e, whisky)}
                        title="Clear rating"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="whisky-details">
                {whisky.distillery && (
                  <div className="detail">
                    <span className="label">Distillery:</span>
                    <span className="value">{whisky.distillery}</span>
                  </div>
                )}
                {whisky.region && (
                  <div className="detail">
                    <span className="label">Region:</span>
                    <span className="value">{whisky.region}</span>
                  </div>
                )}
                {whisky.age && (
                  <div className="detail">
                    <span className="label">Age:</span>
                    <span className="value">{whisky.age}</span>
                  </div>
                )}
                {whisky.abv && (
                  <div className="detail">
                    <span className="label">ABV:</span>
                    <span className="value">{whisky.abv}%</span>
                  </div>
                )}
              </div>

              {ratingData && (
                <div className="rating-summary">
                  <div className="rating-bars">
                    <div className="rating-bar">
                      <span>Nose</span>
                      <div className="bar">
                        <div 
                          className="fill" 
                          style={{ width: `${(ratings[whisky.id].nose / 25) * 100}%` }}
                        />
                      </div>
                      <span>{ratings[whisky.id].nose}</span>
                    </div>
                    <div className="rating-bar">
                      <span>Palate</span>
                      <div className="bar">
                        <div 
                          className="fill" 
                          style={{ width: `${(ratings[whisky.id].palate / 25) * 100}%` }}
                        />
                      </div>
                      <span>{ratings[whisky.id].palate}</span>
                    </div>
                    <div className="rating-bar">
                      <span>Finish</span>
                      <div className="bar">
                        <div 
                          className="fill" 
                          style={{ width: `${(ratings[whisky.id].finish / 25) * 100}%` }}
                        />
                      </div>
                      <span>{ratings[whisky.id].finish}</span>
                    </div>
                    <div className="rating-bar">
                      <span>Overall</span>
                      <div className="bar">
                        <div 
                          className="fill" 
                          style={{ width: `${(ratings[whisky.id].overall / 25) * 100}%` }}
                        />
                      </div>
                      <span>{ratings[whisky.id].overall}</span>
                    </div>
                  </div>
                  
                  {showComments && ratings[whisky.id].comments && (
                    <div className="rating-comments">
                      <p className="comments-text">💭: {ratings[whisky.id].comments}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="card-footer">
                <span className="tap-hint">
                  {ratingData ? 'Tap to edit rating' : 'Tap to rate'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Clear Rating Confirmation Modal */}
      {confirmClear && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Clear Rating</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to clear the rating for:</p>
              <p className="whisky-name-modal"><strong>{confirmClear.name}</strong></p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={cancelClear}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmClearRating}>
                Clear Rating
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Whisky Confirmation Modal */}
      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Delete Whisky</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to permanently delete:</p>
              <p className="whisky-name-modal"><strong>{confirmDelete.name}</strong></p>
              <p className="warning-text">This will delete the whisky and all its ratings. This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={cancelDelete}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteWhisky}>
                Delete Whisky
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default WhiskyList;
